import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import RichText from "components/basic/richText";
import Button from "components/basic/button";
import { headerOffset } from "components/basic/header";
import Typography from "components/basic/typography";
import { useMeQuery, useGoogleLoginMutation } from "generated/graphql";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import GoogleIcon from "@mui/icons-material/Google";
import AppleIcon from "@mui/icons-material/Apple";
import { useGoogleLogin } from "@react-oauth/google";
import { useSnack } from "hooks/useSnack";
import logger from "utils/logger";

interface LoginProps {}

const SignInMethod: React.FC<LoginProps> = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const { t: tCommon } = useTranslation();
  const theme = useTheme();
  const [{ data: loggedInUser, fetching }] = useMeQuery();
  const showSnack = useSnack();
  const [, googleLoginMutation] = useGoogleLoginMutation();

  const googleLogin = useGoogleLogin({
    // Otherwise Google silently reuses the last account, leaving people with
    // several Google accounts no way to pick another.
    prompt: "select_account",
    onSuccess: async (tokenResponse) => {
      try {
        logger.log("Google login successful");

        const result = await googleLoginMutation(
          { accessToken: tokenResponse.access_token },
          { requestPolicy: "network-only" },
        );
        // Not the whole result: its variables hold the Google access token.
        logger.log(
          "Google login mutation result:",
          JSON.stringify(result.data),
        );

        if (result.error) {
          logger.log(
            "Google login mutation network error",
            result.error.message,
          );
          showSnack(
            t("sign_in.google_login_failed_detail", {
              reason: result.error.message,
            }),
            "error",
          );
          return;
        }

        if (result.data?.googleLogin.errors) {
          const msg = result.data.googleLogin.errors
            .map((e) => e.message)
            .join(", ");
          logger.log("Google login mutation field errors", msg);
          showSnack(msg, "error");
          return;
        }

        if (!result.data?.googleLogin.account) {
          logger.log("Google login mutation returned no account", result.data);
          showSnack(
            t("sign_in.google_login_failed_detail", {
              reason: t("sign_in.no_account_returned"),
            }),
            "error",
          );
          return;
        }

        showSnack(
          `Welcome back, ${result.data.googleLogin.account.firstName}!`,
          "success",
        );
        navigate("/");
      } catch (error) {
        logger.log("Unhandled Google login error", String(error));
        showSnack(
          t("sign_in.google_login_failed_detail", {
            reason: t("sign_in.unexpected_error"),
          }),
          "error",
        );
      }
    },
    onError: (error) => {
      logger.log("Google OAuth error", JSON.stringify(error));
      showSnack(
        t("sign_in.google_login_failed_detail", {
          reason: t("sign_in.oauth_error"),
        }),
        "error",
      );
    },
    onNonOAuthError: (error) => {
      logger.log("Google non OAuth error", JSON.stringify(error));
      if (error.type === "popup_failed_to_open") {
        showSnack(
          t("sign_in.google_login_failed_detail", {
            reason: t("sign_in.popup_blocked"),
          }),
          "error",
        );
        return;
      }

      if (error.type === "popup_closed") {
        showSnack(
          t("sign_in.google_login_failed_detail", {
            reason: t("sign_in.popup_closed"),
          }),
          "error",
        );
        return;
      }

      showSnack(t("sign_in.google_login_failed"), "error");
    },
  });

  const handleGoogleSignIn = useCallback(() => {
    logger.log("Google sign-in button clicked");
    try {
      googleLogin();
    } catch (error) {
      logger.log("Google login invocation failed", String(error));
      showSnack(
        t("sign_in.google_login_failed_detail", {
          reason: t("sign_in.unable_to_launch"),
        }),
        "error",
      );
    }
  }, [googleLogin, showSnack, t]);

  const handleHomePageRedirect = useCallback(() => {
    navigate("/");
  }, [navigate]);

  // If user is already logged in and somehow they happen upon this page
  // Then send them back to landing page
  useEffect(() => {
    if (fetching) {
      return;
    }

    if (!loggedInUser?.me) {
      return;
    }

    handleHomePageRedirect();
  }, [fetching, handleHomePageRedirect, loggedInUser]);

  const handleRegisterRedirect = useCallback(() => {
    navigate("/signup");
  }, [navigate]);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        height: `calc(100vh - ${headerOffset(1)})`,
        padding: `0 ${theme.spacing(2)}`,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(2),
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          maxWidth: "500px",
        }}
      >
        <Typography
          variant="h1"
          style={{
            fontWeight: "bold",
            fontSize: "3em",
            marginBottom: theme.spacing(2),
          }}
        >
          {t("common.sign_in")}
        </Typography>

        <div style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing(2),
              marginBottom: theme.spacing(3),
              maxWidth: "800px",
              padding: `0 ${theme.spacing(4)}`,
            }}
          >
            <Button
              onClick={handleGoogleSignIn}
              variant="contained"
              startAdornment={<GoogleIcon />}
            >
              {t("common.continue_google")}
            </Button>

            <Button
              onClick={() => {}}
              variant="contained"
              startAdornment={<AppleIcon />}
              disabled
            >
              {t("common.continue_apple")}
            </Button>

            <Typography variant="body1" align="center">
              {tCommon("ui.or")}
            </Typography>

            <Button
              onClick={() => navigate("?currentPage=signInForm")}
              variant="outlined"
            >
              {t("sign_in.with_email")}
            </Button>

            <Button onClick={handleRegisterRedirect} variant="text">
              <RichText
                t={t}
                i18nKey="common.no_account"
                components={{
                  a: (
                    <span
                      style={{ color: "orange", marginLeft: theme.spacing(1) }}
                    />
                  ),
                }}
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInMethod;
